/**
 * Pincode-first location for LOCSY registration (browser GPS is often wrong on desktop).
 */

/** Known service-area pincodes → town center (lat/lng). */
export const PINCODE_LOCATIONS = {
  '534460': { lat: 17.0417, lng: 80.973, city: 'Chintalapudi', state: 'Andhra Pradesh' },
  '534462': { lat: 17.28, lng: 81.55, city: 'Jangareddigudem', state: 'Andhra Pradesh' },
  '507303': { lat: 17.22, lng: 81.35, city: 'Sathupalli', state: 'Telangana' },
  '507301': { lat: 17.22, lng: 81.35, city: 'Sathupalli', state: 'Telangana' },
  '507302': { lat: 17.22, lng: 81.35, city: 'Sathupalli', state: 'Telangana' },
  '534001': { lat: 16.715, lng: 81.095, city: 'Eluru', state: 'Andhra Pradesh' },
  '520001': { lat: 16.506, lng: 80.647, city: 'Vijayawada', state: 'Andhra Pradesh' },
};

export const normalizePincode = (raw) =>
  String(raw || '')
    .replace(/\D/g, '')
    .slice(0, 6);

export function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Extract human-readable city/village from Nominatim reverse response (India-friendly). */
export function cityFromNominatimAddress(parts = {}) {
  return (
    parts.village ||
    parts.town ||
    parts.city ||
    parts.municipality ||
    parts.suburb ||
    parts.state_district ||
    parts.county ||
    ''
  );
}

/** India Post pincode API — office name + district. */
export async function fetchIndiaPostPincode(pincode) {
  const pin = normalizePincode(pincode);
  if (pin.length !== 6) return null;
  const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
  if (!res.ok) return null;
  const data = await res.json();
  if (data?.Status !== 'Success' || !Array.isArray(data.PostOffice)?.length) {
    return null;
  }
  const office = data.PostOffice[0];
  return {
    pincode: pin,
    city: office.Name || office.District || '',
    district: office.District || '',
    state: office.State || 'Andhra Pradesh',
  };
}

/** Forward geocode pincode area via Nominatim search. */
export async function geocodePincodeArea(pincode, cityHint, stateHint) {
  const pin = normalizePincode(pincode);
  const q = [cityHint, pin, stateHint || 'Andhra Pradesh', 'India']
    .filter(Boolean)
    .join(', ');
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
  const res = await fetch(url, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'en',
    },
  });
  if (!res.ok) return null;
  const rows = await res.json();
  if (!Array.isArray(rows) || !rows.length) return null;
  const hit = rows[0];
  return {
    lat: parseFloat(hit.lat),
    lng: parseFloat(hit.lon),
    displayName: hit.display_name || '',
  };
}

/**
 * Resolve lat/lng + city/state for a 6-digit pincode.
 * Uses local table first, then India Post + Nominatim.
 */
export async function resolveLocationFromPincode(pincode) {
  const pin = normalizePincode(pincode);
  if (pin.length !== 6) {
    return { ok: false, message: 'Enter a valid 6-digit pincode' };
  }

  const known = PINCODE_LOCATIONS[pin];
  if (known) {
    return {
      ok: true,
      pincode: pin,
      lat: known.lat,
      lng: known.lng,
      city: known.city,
      state: known.state,
      source: 'local',
    };
  }

  let post = null;
  try {
    post = await fetchIndiaPostPincode(pin);
  } catch {
    post = null;
  }

  let geo = null;
  try {
    geo = await geocodePincodeArea(
      pin,
      post?.city || post?.district,
      post?.state
    );
  } catch {
    geo = null;
  }

  if (!geo?.lat || !geo?.lng) {
    return {
      ok: false,
      message:
        'Could not find map location for this pincode. Use GPS or type city manually.',
    };
  }

  return {
    ok: true,
    pincode: pin,
    lat: geo.lat,
    lng: geo.lng,
    city: post?.city || post?.district || '',
    state: post?.state || '',
    source: 'geocoded',
  };
}

export function gpsMismatchWithPincode(lat, lng, pincode, thresholdKm = 25) {
  const pin = normalizePincode(pincode);
  const known = PINCODE_LOCATIONS[pin];
  if (!known || lat == null || lng == null) return null;
  const km = haversineKm(lat, lng, known.lat, known.lng);
  if (km <= thresholdKm) return null;
  return {
    km: Math.round(km),
    expectedCity: known.city,
    pincode: pin,
  };
}
